/**
 * React Query hooks for submitted actions.
 * React Query hooks for the corresponding entity.
 */

import { QueryClient, useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";

export type SubmittedActionEntry = {
  guid: string;
  deadline: string;
  categoryId: number;
  category: string;
  actionId: number;
  action: string;
  requiredEvidence: string;
  programmeId: string;
  date: string;
  email: string;
  detailQuestion: string;
  submittedDetails: string;
  status: string;
};

/**
 * API for fetching submitted actions
 */
async function fetchSubmittedActionsByEmail(
  email: string
): Promise<SubmittedActionEntry[]> {
  const res = await fetch(
    `${
      import.meta.env.VITE_API_ENDPOINT
    }/v1/submittedActions?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    }
  );
  // Return empty array if API returns error (e.g., 404, 500)
  if (!res.ok) {
    console.warn(`Failed to fetch submitted actions: ${res.status}`);
    return [];
  }
  return (await res.json()) as SubmittedActionEntry[];
}

/**
 * Hook to fetch submitted actions by email
 */
export function useSubmittedActions(email: string | undefined) {
  return useQuery({
    queryKey: queryKeys.programmeActions.submitted(email ?? ""),
    queryFn: async () => {
      if (!email) return [];
      return await fetchSubmittedActionsByEmail(email);
    },
    enabled: !!email,
  });
}

/**
 * Fetch submitted actions by email (used during initialization)
 */
export async function fetchSubmittedActionsQuery(
  email: string
): Promise<SubmittedActionEntry[]> {
  return await fetchSubmittedActionsByEmail(email);
}

/**
 * Reset submitted actions query cache
 */
export function resetSubmittedActionsQueryCache(
  queryClient: QueryClient
): void {
  queryClient.removeQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === "programmeActions" &&
      query.queryKey[1] === "submitted",
  });
}
